/**
 * Create a project via Git, ZIP, or staging URL intake.
 */

import { useState, type CSSProperties } from 'react'
import { api } from '../lib/api'
import { Modal } from './design-system/Modal'
import { Button } from './design-system/Button'
import { useLocale } from '../contexts/LocaleContext'

type IntakeMode = 'git' | 'zip' | 'staging'

interface CreateProjectDialogProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (projectId: string) => void
}

export function CreateProjectDialog({ isOpen, onClose, onCreated }: CreateProjectDialogProps) {
  const { t } = useLocale()
  const [mode, setMode] = useState<IntakeMode>('git')
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [repoUrl, setRepoUrl] = useState('')
  const [gitRef, setGitRef] = useState('')
  const [stagingUrl, setStagingUrl] = useState('')
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setDisplayName('')
    setDescription('')
    setRepoUrl('')
    setGitRef('')
    setStagingUrl('')
    setZipFile(null)
    setError(null)
  }

  async function handleCreate() {
    if (!displayName.trim()) {
      setError(t('dialog.nameRequired'))
      return
    }
    try {
      setLoading(true)
      setError(null)
      let result
      if (mode === 'git') {
        if (!repoUrl.trim()) {
          setError(t('dialog.repoRequired'))
          setLoading(false)
          return
        }
        result = await api.createGitIntake({
          repo_url: repoUrl.trim(),
          display_name: displayName.trim(),
          ref: gitRef.trim() || undefined,
          description: description.trim() || undefined,
        })
      } else if (mode === 'zip') {
        if (!zipFile) {
          setError(t('dialog.zipRequired'))
          setLoading(false)
          return
        }
        result = await api.createZipIntake(zipFile, displayName.trim(), description.trim() || undefined)
      } else {
        if (!stagingUrl.trim()) {
          setError(t('dialog.stagingRequired'))
          setLoading(false)
          return
        }
        result = await api.createStagingIntake({
          staging_url: stagingUrl.trim(),
          display_name: displayName.trim(),
          description: description.trim() || undefined,
        })
      }
      if (!result.project_id) {
        throw new Error(result.error_message || t('dialog.intakeNoProject'))
      }
      reset()
      onCreated(result.project_id)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dialog.failedCreate'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset()
        onClose()
      }}
      title={t('proj.add')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={loading} data-testid="create-project-submit">
            {loading ? t('dialog.creating') : t('proj.create')}
          </Button>
        </>
      }
    >
      <div data-testid="create-project-dialog" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {error && (
          <p className="text-sm" style={{ color: 'var(--reviewer-danger)', margin: 0 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['git', 'zip', 'staging'] as IntakeMode[]).map((item) => (
            <Button
              key={item}
              variant={mode === item ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setMode(item)}
            >
              {item === 'git' ? t('proj.git') : item === 'zip' ? t('proj.zipBtn') : t('proj.staging')}
            </Button>
          ))}
        </div>
        <label className="text-sm">
          {t('proj.displayName')}
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-testid="create-project-name"
            style={inputStyle}
          />
        </label>
        <label className="text-sm">
          {t('proj.description')}
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
        </label>
        {mode === 'git' && (
          <>
            <label className="text-sm">
              {t('proj.gitUrl')}
              <input
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/org/repo"
                style={inputStyle}
              />
            </label>
            <label className="text-sm">
              {t('proj.gitRef')}
              <input value={gitRef} onChange={(e) => setGitRef(e.target.value)} placeholder="main" style={inputStyle} />
            </label>
          </>
        )}
        {mode === 'zip' && (
          <label className="text-sm">
            {t('proj.zip')}
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setZipFile(e.target.files?.[0] || null)}
            />
          </label>
        )}
        {mode === 'staging' && (
          <label className="text-sm">
            {t('proj.stagingUrl')}
            <input
              value={stagingUrl}
              onChange={(e) => setStagingUrl(e.target.value)}
              placeholder="https://staging.example.com"
              style={inputStyle}
            />
          </label>
        )}
      </div>
    </Modal>
  )
}

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: '6px',
  padding: '8px 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  backgroundColor: 'var(--reviewer-bg)',
  border: '1px solid var(--reviewer-border)',
  color: 'var(--reviewer-text)',
}
